import { Link, Outlet, createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';

import { useGetPrimaryAssetQuery } from '@/api/api-hooks';
import { ChatProvider } from '@/components/Chat/ChatContext';
import { PdfRightSidebar } from '@/components/CodebaseView/PdfRightSidebar';
import { PdfRightSidebarTabType } from '@/components/CodebaseView/PdfRightSidebarTabType';
import { FavoriteToggleButton } from '@/components/FavoriteToggleButton';
import { FlexiblePanelLayout } from '@/components/FlexiblePanelLayout/FlexiblePanelLayout';
import { LeftMainHeader } from '@/components/FlexiblePanelLayout/LeftMainHeader';
import { LayoutDefault } from '@/components/LayoutDefault';
import { LayoutHeader } from '@/components/LayoutHeader';
import { NotFound404 } from '@/components/NotFound404';
import {
  getSourceAssetVersionNodeIds,
  transformVersionRecordData,
} from '@/components/UserDefinedScope/SourceAssetRecord';
import { UserDefinedScope } from '@/components/UserDefinedScope/UserDefinedScope';
import { LeftSidebarPortal } from '@/components/portals/LeftSidebarPortal';
import { BreadcrumbLink } from '@/components/ui/breadcrumb';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useLayout } from '@/contexts/LayoutContext';
import { MemoryTunerProvider, useMemoryTunerActions } from '@/contexts/MemoryTunerContext';
import { PageProvider } from '@/contexts/PageContext';
import { PdfProvider, usePdf } from '@/contexts/PdfContext';
import { TabProvider } from '@/contexts/TabContext';
import { useTunerContext } from '@/contexts/TunerContext';
import { VersionsProvider, useVersions } from '@/contexts/VersionsContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePdfRoute } from '@/hooks/usePdfRoute';
import { searchParam } from '@/utils/router';

const pdfSearchSchema = z.object({
  versionId: searchParam().optional(),
});

export const Route = createFileRoute('/$organizationName/pdf/$pdfVersionNodeId')({
  component: ProvidedPdfLayout,
  validateSearch: zodValidator(pdfSearchSchema),
});

const PdfLayout: React.FC = () => {
  const { pdfVersionNodeId, organizationName, tab, isPage, pathArray } = usePdfRoute();
  const { isPdfLoading, pdfId, pdfName } = usePdf();
  const { versionId } = Route.useSearch();
  const { selectedVersion } = useVersions();
  const { isLargeScreen } = useLayout();
  const { value: isChatUiEnabled } = useFeatureFlag(FEATURE_FLAGS.CHAT_UI);
  const { data: primaryAssetData } = useGetPrimaryAssetQuery(
    { id: pdfId ?? '', limit: 1, offset: 0 },
    { enabled: !!pdfId }
  );
  const isUserFavorited = primaryAssetData?.results[0]?.is_user_favorited;

  const { documentSources, openUserDefinedScope, setOpenUserDefinedScope } = useTunerContext();
  const { setDocumentSources } = useMemoryTunerActions();

  const chatSourceVersionNodeIds = useMemo(() => {
    return getSourceAssetVersionNodeIds(documentSources);
  }, [documentSources]);

  const tabs = useMemo(() => {
    return [
      {
        key: 'file',
        displayName: 'File',
        to: '/$organizationName/pdf/$pdfVersionNodeId' as const,
        params: {
          organizationName: organizationName,
          pdfVersionNodeId: pdfVersionNodeId,
        },
        search: versionId ? { versionId } : undefined,
      },

      {
        key: 'autodocs',
        displayName: 'Autodocs',
        to: '/$organizationName/pdf/$pdfVersionNodeId/autodocs' as const,
        params: {
          organizationName: organizationName,
          pdfVersionNodeId: pdfVersionNodeId,
        },
        search: versionId ? { versionId } : undefined,
        showNewBadge: true,
      },
      {
        key: 'settings',
        displayName: 'Settings',
        to: '/$organizationName/pdf/$pdfVersionNodeId/settings/general' as const,
        params: {
          organizationName: organizationName,
          pdfVersionNodeId: pdfVersionNodeId,
        },
        search: versionId ? { versionId } : undefined,
      },
    ];
  }, [organizationName, pdfVersionNodeId, versionId]);

  const tabDisplayName = tabs.find((t) => t.key === tab)?.displayName;

  useEffect(() => {
    if (pdfName && tabDisplayName) {
      const pageTitle = `${pdfName} - ${tabDisplayName}`;
      document.title = pageTitle;
    }
  }, [pdfName, tabDisplayName]);

  useEffect(() => {
    // Pages sources are handled by usePageTuner in `/autodocs/page/$pageId.tsx`
    // This also restores the sources when navigating away from the page and back to the pdf.
    if (!isPage) {
      // Transform the selected version data into a SourceAssetRecord.
      const sourceAssetRecords = selectedVersion ? transformVersionRecordData([selectedVersion]) : [];
      setDocumentSources(sourceAssetRecords);
    }
  }, [selectedVersion, setDocumentSources, isPage]);

  // `!pathArray[0]` is checking for the autodocId which would indicate this is the details view and we DO want to show the left panel.
  const showLeftPanel = isLargeScreen && tab === 'autodocs' && !!pathArray[0];

  if (!isPdfLoading && !pdfName) {
    return <NotFound404 />;
  }

  return (
    <div className="flex flex-col">
      <LayoutDefault
        navbarTitle={pdfName}
        header={
          <LayoutHeader
            right={pdfId ? <FavoriteToggleButton sourceId={pdfId} isUserFavorited={isUserFavorited} /> : null}
          >
            <BreadcrumbLink asChild className="truncate">
              <Link to="." aria-current="page">
                {pdfName}
              </Link>
            </BreadcrumbLink>
          </LayoutHeader>
        }
      >
        <LeftSidebarPortal.Provider>
          <FlexiblePanelLayout
            showBorder
            pageType="pdf"
            className={isLargeScreen ? 'h-[calc(100vh-44px)]' : 'h-[calc(100vh-101px)]'}
            leftPanelContent={<LeftSidebarPortal.Slot />}
            leftPanelContentClassName="px-0"
            leftMainHeader={
              // FUTURE: add a better loading state here.
              isPdfLoading ? null : <LeftMainHeader selectedTab={tab} tabs={tabs} />
            }
            initialLeftPanelWidth={0}
            // CD: Ideally the individual page/routes would control the state of `hideLeftPanel`
            // but the only way I can think to do it is by adding it to context, and not sure it's worth it (yet).
            hideLeftPanel={!showLeftPanel}
            // Chat is the right panel's only tab on a plain PDF view; pages add Sources/Properties.
            hideRightPanel={tab === 'settings' || (!isChatUiEnabled && !isPage)}
            // FUTURE: add a better loading state here.
            mainPanelContent={isPdfLoading ? null : <Outlet />}
            initialRightPanelWidth={308} // 300px + 8px margin
            showRightPanelHeaderDivider={false}
            rightPanelClassName="h-full"
            rightPanelContent={<PdfRightSidebar isPage={isPage} sourceVersionNodeIds={chatSourceVersionNodeIds} />}
            rightPanelMinWidthClosed={56} // 48px + 8px margin
            hideExpandCollapseButton
          />
          {openUserDefinedScope && !isPage && (
            <UserDefinedScope
              open={openUserDefinedScope}
              onOpen={setOpenUserDefinedScope}
              onSourcesChanged={setDocumentSources}
              sources={documentSources}
            />
          )}
        </LeftSidebarPortal.Provider>
      </LayoutDefault>
    </div>
  );
};

function ProvidedPdfLayout() {
  const { pageVersionNodeId, pdfVersionNodeId } = usePdfRoute();

  return (
    <PageProvider pageVersionNodeId={pageVersionNodeId}>
      <MemoryTunerProvider>
        <PdfProvider pdfVersionNodeId={pdfVersionNodeId}>
          <InnerProvidedPdfLayout />
        </PdfProvider>
      </MemoryTunerProvider>
    </PageProvider>
  );
}

function InnerProvidedPdfLayout() {
  const { pdfId } = usePdf();
  const query = Route.useSearch();
  const { versionId } = query;

  return (
    <VersionsProvider sourceId={pdfId} versionId={versionId}>
      <ChatProvider>
        <TabProvider<PdfRightSidebarTabType> defaultTab="chat">
          <PdfLayout />
        </TabProvider>
      </ChatProvider>
    </VersionsProvider>
  );
}
